/**
 * A pack piece's face, painted in the logo's own background colour.
 *
 * A scraped logo that ships on a solid canvas (a black square, a yellow
 * rectangle) used to land on the pale card stock as a stray tile — the logo's
 * ground and the paper never met. This does what template-monster's header
 * already does (`apps/template-monster/lib/headerSurface.ts`, fnd-782n): the
 * whole face takes the logo's colour, the logo blends in seamlessly, and every
 * line of type flips to whichever ink actually reads on it — white on black,
 * near-black on yellow.
 *
 * The generator measures that colour and writes `business.theme.logoBgHex`
 * only when the logo canvas is solid, opaque and NOT white (see
 * apps/clonerator/lib/remove-bg.ts `measureLogoBackground`). Absent, invalid
 * or near-white → `resolveLogoSurface` returns null and the piece renders on
 * its usual stock, byte-for-byte what it always was.
 *
 * Zero imports on purpose: BusinessCard and PackItems both read it, and the
 * logic is a mirror of headerSurface so the card and the header agree.
 */

/** Anything at or above this luminance counts as "white enough to leave alone". */
export const NEAR_WHITE_LUMINANCE = 0.85;

/** The near-black ink the pack prints with on light stock. */
export const PACK_INK = '#1e2228';

export interface LogoSurface {
  /** The measured colour, normalised to `#rrggbb`. */
  bg: string;
  /** WCAG relative luminance of `bg`, 0 (black) → 1 (white). */
  luminance: number;
  /** True when white type reads better on this colour than near-black does. */
  isDark: boolean;
  /** Primary text on this surface. */
  ink: string;
  /** Secondary text (owner line, eyebrow, phone on the card). */
  muted: string;
  /** Tertiary text (the "Est." tag, footnotes). */
  faint: string;
  /** Hairline rules on this surface. */
  rule: string;
  /**
   * The brand colour the piece prints its domain/accent in — when the
   * theme's primary would vanish against this surface, the ink stands in.
   */
  accent: (brandHex: string) => string;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Accepts `#rrggbb`, `rrggbb`, `#rgb` and `rgb`; returns `#rrggbb` lowercase,
 * or null for anything that is not a colour. Deliberately strict — a bad value
 * in the bundle must fall back to the plain stock, never to a broken face.
 */
export function normalizeHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const raw = value.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw
      .split('')
      .map(c => c + c)
      .join('')
      .toLowerCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toLowerCase()}`;
  return null;
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours (1 → 21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Resolve a logo's measured background into a face surface, or null to keep
 * the plain stock. Null for: missing/invalid values, and colours that are
 * already white or near-white (a white-canvas logo on white stock is what the
 * fleet prints today and must stay pixel-identical).
 */
export function resolveLogoSurface(value: unknown): LogoSurface | null {
  const bg = normalizeHex(value);
  if (!bg) return null;
  const luminance = relativeLuminance(bg);
  if (luminance >= NEAR_WHITE_LUMINANCE) return null;
  // Pick the ink by measured contrast rather than a luminance threshold, so a
  // mid-tone brand colour gets whichever of the two actually reads better.
  const isDark = contrastRatio(bg, '#ffffff') >= contrastRatio(bg, PACK_INK);
  const ink = isDark ? '#ffffff' : PACK_INK;
  return {
    bg,
    luminance,
    isDark,
    ink,
    muted: isDark ? 'rgba(255,255,255,0.78)' : 'rgba(30,34,40,0.72)',
    faint: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(30,34,40,0.5)',
    rule: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(30,34,40,0.16)',
    // 3:1 is the WCAG floor for large/graphic elements — enough for a domain
    // line in mono; below it the brand colour is invisible on this face.
    accent: (brandHex: string) => (contrastRatio(bg, brandHex) >= 3 ? brandHex : ink),
  };
}
